import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Icon } from '@fluentui/react'

import { CosmosDBStatus, agencyCandidateMessagesUnreadCount, logout, switchAgency } from '../../api'
import { CVUploadPanel } from '../../components/CVUploadPanel'
import Contoso from '../../assets/Contoso.svg'
import { AppStateContext } from '../../state/AppProvider'
import Chat from '../chat/Chat'

import styles from './Layout.module.css'

interface NavConfig {
  path: string
  label: string
  icon: string
  group: 'primary' | 'secondary'
}

const DRAWER_MIN_WIDTH = 360
const DRAWER_DEFAULT_WIDTH = 580
const DRAWER_WIDTH_KEY = 'cdreams.chatDrawer.width'

const clampDrawerWidth = (value: number) => {
  const max = Math.max(DRAWER_MIN_WIDTH, Math.round(window.innerWidth * 0.96))
  return Math.min(max, Math.max(DRAWER_MIN_WIDTH, Math.round(value)))
}

const NAV: NavConfig[] = [
  { path: '/', label: 'Home', icon: 'Home', group: 'primary' },
  { path: '/candidates', label: 'Candidates', icon: 'People', group: 'primary' },
  { path: '/clients', label: 'Clients', icon: 'CityNext', group: 'primary' },
  { path: '/market', label: 'Market', icon: 'SearchNearby', group: 'primary' },
  { path: '/jobs', label: 'Jobs', icon: 'Suitcase', group: 'primary' },
  { path: '/emails', label: 'Email', icon: 'Mail', group: 'secondary' },
  { path: '/candidate-messages', label: 'Messages', icon: 'ChatBot', group: 'secondary' },
  { path: '/activity-logs', label: 'Activity', icon: 'ReportDocument', group: 'secondary' },
  { path: '/help', label: 'Help', icon: 'Help', group: 'secondary' },
]

interface NavItemProps {
  icon: string
  label: string
  active: boolean
  badge?: number
  onClick: () => void
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active, badge, onClick }) => (
  <button
    type="button"
    className={`${styles.navItem} ${active ? styles.active : ''}`}
    onClick={onClick}
    title={label}
    aria-current={active ? 'page' : undefined}
  >
    <Icon iconName={icon} className={styles.navIcon} />
    <span>{label}</span>
    {badge && badge > 0 ? <span className={styles.navBadge}>{badge > 99 ? '99+' : badge}</span> : null}
  </button>
)

const Layout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [candidateMessagesUnread, setCandidateMessagesUnread] = useState<number>(0)
  const [chatOpen, setChatOpen] = useState<boolean>(false)
  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return DRAWER_DEFAULT_WIDTH
    const saved = window.localStorage.getItem(DRAWER_WIDTH_KEY)
    const parsed = saved ? Number.parseInt(saved, 10) : NaN
    return Number.isFinite(parsed) ? clampDrawerWidth(parsed) : DRAWER_DEFAULT_WIDTH
  })
  const [resizing, setResizing] = useState<boolean>(false)
  const resizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null)
  const appStateContext = useContext(AppStateContext)
  const ui = appStateContext?.state.frontendSettings?.ui
  const user = appStateContext?.state.user

  const [agencyMenuOpen, setAgencyMenuOpen] = useState<boolean>(false)
  const [switching, setSwitching] = useState<boolean>(false)

  const handleLogout = async () => {
    await logout()
    appStateContext?.dispatch({ type: 'LOGOUT' })
    navigate('/login')
  }

  const handleSwitchAgency = async (agencyid: number) => {
    if (switching || agencyid === user?.agencyid) {
      setAgencyMenuOpen(false)
      return
    }
    setSwitching(true)
    const result = await switchAgency(agencyid)
    setSwitching(false)
    setAgencyMenuOpen(false)
    if (result.success && result.user) {
      appStateContext?.dispatch({ type: 'SET_USER', payload: result.user })
      // The session's active agency changed server-side; reload so every
      // agency-scoped view refetches against the new agency.
      window.location.reload()
    }
  }

  const handleHistoryClick = () => {
    appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
  }

  // Poll for unread candidate messages so the nav badge stays fresh across
  // replicas without a websocket. 20s cadence is enough for an inbox.
  useEffect(() => {
    if (!appStateContext?.state.isAuthenticated) return
    let cancelled = false
    const tick = async () => {
      const n = await agencyCandidateMessagesUnreadCount()
      if (!cancelled) setCandidateMessagesUnread(n)
    }
    tick()
    const id = window.setInterval(tick, 20000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [appStateContext?.state.isAuthenticated])

  // Close drawer on route change so it doesn't linger when the user navigates
  useEffect(() => {
    setChatOpen(false)
  }, [location.pathname])

  // Esc closes the drawer
  useEffect(() => {
    if (!chatOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChatOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chatOpen])

  // Persist the drawer width across sessions so a user-set size sticks
  useEffect(() => {
    try {
      window.localStorage.setItem(DRAWER_WIDTH_KEY, String(drawerWidth))
    } catch {
      /* localStorage may be blocked — non-fatal */
    }
  }, [drawerWidth])

  // Re-clamp width if the viewport shrinks below the saved size
  useEffect(() => {
    const onResize = () => setDrawerWidth(w => clampDrawerWidth(w))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Drag-to-resize: pointer math runs against the right edge of the viewport
  // since the drawer is right-anchored. Pull leftward = wider.
  const beginResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    resizeStateRef.current = { startX: e.clientX, startWidth: drawerWidth }
    setResizing(true)
    const onMove = (ev: MouseEvent) => {
      const state = resizeStateRef.current
      if (!state) return
      const delta = state.startX - ev.clientX
      setDrawerWidth(clampDrawerWidth(state.startWidth + delta))
    }
    const onUp = () => {
      resizeStateRef.current = null
      setResizing(false)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [drawerWidth])

  const initials = (() => {
    const source = user?.full_name || user?.username || ''
    const parts = source.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return 'CD'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  })()

  const historyOpen = appStateContext?.state?.isChatHistoryOpen
  const historyAvailable =
    appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured
  const historyVisible = historyAvailable && chatOpen

  const primaryNav = NAV.filter(n => n.group === 'primary')
  const secondaryNav = NAV.filter(n => n.group === 'secondary')

  return (
    <div className={styles.layout}>
      <header className={styles.topbar} role="banner">
        <Link to="/" className={styles.brand}>
          <img
            src={ui?.logo ? ui.logo : Contoso}
            className={styles.brandLogo}
            alt=""
            aria-hidden="true"
          />
          <h1 className={styles.brandTitle}>{ui?.title || 'CDreams'}</h1>
        </Link>

        <nav className={styles.nav} aria-label="Primary navigation">
          {primaryNav.map(item => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              active={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            />
          ))}
          <div className={styles.navDivider} aria-hidden="true" />
          {secondaryNav.map(item => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              active={location.pathname === item.path}
              badge={item.path === '/candidate-messages' ? candidateMessagesUnread : undefined}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.askChatButton}
            onClick={() => setChatOpen(true)}
            title="Open AI assistant"
          >
            <Icon iconName="ChatBot" className={styles.askChatIcon} />
            <span>Ask CDreams</span>
          </button>
          {historyVisible && (
            <button
              type="button"
              className={styles.historyToggle}
              onClick={handleHistoryClick}
              title={historyOpen ? 'Hide chat history' : 'Show chat history'}
            >
              <Icon iconName="History" />
              <span>{historyOpen ? 'Hide history' : 'History'}</span>
            </button>
          )}
          {user && (
            <div style={{ position: 'relative' }}>
              <div
                className={styles.userPill}
                title={`${user.full_name || user.username} · ${user.agency_name}`}
                onClick={() => (user.agencies?.length || 0) > 1 && setAgencyMenuOpen(o => !o)}
                style={{ cursor: (user.agencies?.length || 0) > 1 ? 'pointer' : 'default' }}
              >
                <div className={styles.userAvatar} aria-hidden="true">{initials}</div>
                <div className={styles.userText}>
                  <span className={styles.userName}>{user.full_name || user.username}</span>
                  <span className={styles.userAgency}>
                    {user.agency_name}
                    {(user.agencies?.length || 0) > 1 ? ' ▾' : ''}
                  </span>
                </div>
              </div>
              {agencyMenuOpen && (user.agencies?.length || 0) > 1 && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 6,
                    minWidth: 200,
                    background: '#fff',
                    border: '1px solid #e1e1e1',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}
                >
                  {user.agencies.map(a => (
                    <button
                      key={a.agencyid}
                      type="button"
                      role="menuitem"
                      disabled={switching}
                      onClick={() => handleSwitchAgency(a.agencyid)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 14px',
                        border: 'none',
                        background: a.agencyid === user.agencyid ? '#f3f2f1' : 'transparent',
                        fontWeight: a.agencyid === user.agencyid ? 600 : 400,
                        cursor: switching ? 'default' : 'pointer'
                      }}
                    >
                      {a.agency_name}
                      {a.agencyid === user.agencyid ? ' ✓' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button type="button" className={styles.signOut} onClick={handleLogout} title="Sign out" aria-label="Sign out">
            <Icon iconName="SignOut" />
          </button>
        </div>
      </header>

      <main className={styles.contentArea}>
        <div className={styles.pageContent}>
          <Outlet />
        </div>
      </main>

      {/* Chat drawer — available on every screen */}
      <div
        className={`${styles.chatOverlay} ${chatOpen ? styles.open : ''}`}
        onClick={() => setChatOpen(false)}
        aria-hidden={!chatOpen}
      />
      <aside
        className={`${styles.chatDrawer} ${chatOpen ? styles.open : ''}`}
        style={chatOpen ? { width: `${drawerWidth}px` } : undefined}
        aria-hidden={!chatOpen}
        aria-label="AI Assistant"
      >
        <div
          className={`${styles.drawerResizer} ${resizing ? styles.resizing : ''}`}
          onMouseDown={beginResize}
          onDoubleClick={() => setDrawerWidth(DRAWER_DEFAULT_WIDTH)}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat panel"
          aria-valuenow={drawerWidth}
          aria-valuemin={DRAWER_MIN_WIDTH}
          title="Drag to resize · double-click to reset"
        />
        <div className={styles.drawerHeader}>
          <Icon iconName="ChatBot" className={styles.drawerHeaderIcon} />
          <span className={styles.drawerHeaderText}>
            AI Assistant
            <span className={styles.drawerHeaderSub}>· ask anything</span>
          </span>
          <button
            type="button"
            className={styles.drawerClose}
            onClick={() => setChatOpen(false)}
            aria-label="Close chat"
          >
            <Icon iconName="Cancel" />
          </button>
        </div>
        <div className={styles.drawerBody}>
          <Chat />
        </div>
      </aside>

      <CVUploadPanel />
    </div>
  )
}

export default Layout
